import type { ConversationThread, DeckCheck, DeckCheckRow, ReviewSchedule, ReviewSection } from '../../types';
import type { StatusTone } from '../../design/primitives';
import { moveText, reviewSections, reviewStage, sectionTitle } from '../../stores/slices/reviewsSlice';

/**
 * The living review as one page (#568): what the page reads from a review's
 * doc — the deck check's results, the tile groups, the week so far, the
 * card's status. Pure: every line here is derived from the doc the server
 * keeps, never from what a client once graded.
 */

export type DeckResult = 'matches' | 'stale' | 'definition' | 'mapped' | 'needs' | 'skipped';

export const DECK_RESULT_LABEL: Record<DeckResult, string> = {
  matches: 'Matches', stale: 'Stale', definition: 'Different definition', mapped: 'Mapped', needs: 'Need you', skipped: 'Not graded',
};

export const DECK_RESULT_TONE: Record<DeckResult, StatusTone> = {
  matches: 'go', stale: 'stop', definition: 'draft', mapped: 'done', needs: 'stop', skipped: 'off',
};

/** A reconcile verdict as the deck check says it: the deck's number reproduced, stale, read under another definition, or yours to settle. */
export function deckResult(row: DeckCheckRow): DeckResult {
  switch (row.verdict) {
    case 'match': return 'matches';
    case 'stale': return 'stale';
    case 'ambiguous': return 'definition';
    case 'skipped': return 'skipped';
    default: return 'needs';
  }
}

/** The section a graded number became when the review was built: the pin keeps the grading's key as its origin. */
export function sectionForDeckRow(thread: ConversationThread | null | undefined, row: DeckCheckRow): ReviewSection | null {
  if (!row.key) return null;
  return reviewSections(thread).find((s) => s.kind === 'claim'
    && (s.origin_key === row.key || s.key === row.key || (s.key_history || []).some((h) => h.key === row.key))) || null;
}

/** A definition mapped onto the row's number from the review, when it is the one the deck's value picked. */
export function mappedDefinition(row: DeckCheckRow, section: ReviewSection | null): ReviewSection['adjusted'] | null {
  if (!section?.adjusted?.mapping_id || !row.resolved?.metric_qid) return null;
  return section.claim?.grounding?.metric_qid === row.resolved.metric_qid ? section.adjusted : null;
}

/** The mapping reproduced the deck's number when it was saved — a mapping that did not is on record as a mismatch. */
export function mappingReproduced(adjusted: ReviewSection['adjusted'] | null | undefined): boolean {
  return adjusted?.verdict === 'match' || adjusted?.verdict === 'ambiguous';
}

/**
 * Whether a mapping's verdict is about the deck's number: the adjust grades
 * the section as it stands, which is the deck's number until the section's
 * first refresh after the check — this week's after it.
 */
export function gradedAgainstDeck(adjusted: ReviewSection['adjusted'] | null | undefined, check: DeckCheck | null | undefined): boolean {
  const basis = adjusted?.basis_refreshed_at;
  if (!basis) return true;
  return !!check?.checked_at && Date.parse(basis) <= Date.parse(check.checked_at);
}

/**
 * What the row reads now: a different definition the reader mapped for this
 * deck matches — only when the re-derivation under it reproduced the deck's
 * number; a mapping that did not needs you, whoever signed it. A mapping made
 * after the section refreshed was graded against this week's number, so it
 * says neither: it is Mapped.
 */
export function currentResult(row: DeckCheckRow, section: ReviewSection | null, check?: DeckCheck | null): DeckResult {
  const result = deckResult(row);
  const mapped = result === 'definition' ? mappedDefinition(row, section) : null;
  if (!mapped) return result;
  if (!gradedAgainstDeck(mapped, check)) return 'mapped';
  return mappingReproduced(mapped) ? 'matches' : 'needs';
}

export interface DeckCounts { total: number; matches: number; stale: number; definition: number; mapped: number; needs: number; skipped: number }

export function deckCounts(thread: ConversationThread | null | undefined, check: DeckCheck | null | undefined): DeckCounts {
  const counts: DeckCounts = { total: 0, matches: 0, stale: 0, definition: 0, mapped: 0, needs: 0, skipped: 0 };
  for (const row of check?.rows || []) {
    counts.total += 1;
    counts[currentResult(row, sectionForDeckRow(thread, row), check)] += 1;
  }
  return counts;
}

/** "8 numbers · 6 match · 1 stale · 1 different definition" — only the kinds there are. */
export function deckCountsText(c: DeckCounts): string {
  const parts = [`${c.total} number${c.total === 1 ? '' : 's'}`, `${c.matches} match`];
  if (c.stale) parts.push(`${c.stale} stale`);
  if (c.definition) parts.push(`${c.definition} different definition${c.definition === 1 ? '' : 's'}`);
  if (c.mapped) parts.push(`${c.mapped} mapped`);
  if (c.needs) parts.push(`${c.needs} need${c.needs === 1 ? 's' : ''} you`);
  if (c.skipped) parts.push(`${c.skipped} not graded`);
  return parts.join(' · ');
}

export function deckName(check: DeckCheck | null | undefined): string {
  return check?.artifact?.name || check?.artifact?.file || 'the deck';
}

/**
 * The deck called this tile stale, and it has not been refreshed since the
 * check: its real value, as the fact table has it. A refresh after the check
 * re-derived it, and the flag goes.
 */
export function staleTile(thread: ConversationThread | null | undefined, section: ReviewSection): string | null {
  const check = thread?.doc?.deck_check;
  if (!check) return null;
  const row = check.rows.find((r) => r.verdict === 'stale' && sectionForDeckRow(thread, r)?.id === section.id);
  if (!row) return null;
  if (section.refreshed_at && check.checked_at && Date.parse(section.refreshed_at) > Date.parse(check.checked_at)) return null;
  return row.stale?.fact_rendered || '';
}

export interface TileGroup { title: string; tiles: ReviewSection[] }

/** The scorecard's tiles under the template's headings; a tile names none (or one the layout lost) → the first. */
export function tileGroups(slot: ReviewSection | null | undefined, tiles: ReviewSection[]): TileGroup[] {
  const titles = (slot?.groups || []).filter(Boolean);
  if (!titles.length) return [{ title: slot?.title || 'Key numbers', tiles }];
  return titles.map((title) => ({
    title, tiles: tiles.filter((t) => (t.group && titles.includes(t.group) ? t.group : titles[0]) === title),
  }));
}

const DAYS = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'];

/** When a weekly schedule next fires, UTC; null for a review that follows its report or has no schedule. */
export function nextRefresh(schedule: ReviewSchedule | null | undefined, now: Date): Date | null {
  if (!schedule?.weekday || !schedule.at || schedule.follow) return null;
  const day = DAYS.indexOf(schedule.weekday.slice(0, 3).toLowerCase());
  const [h, m] = schedule.at.split(':').map(Number);
  if (day < 0 || !Number.isFinite(h) || !Number.isFinite(m)) return null;
  const next = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate(), h, m));
  next.setUTCDate(next.getUTCDate() + ((day - now.getUTCDay() + 7) % 7));
  if (next.getTime() <= now.getTime()) next.setUTCDate(next.getUTCDate() + 7);
  return next;
}

export function utcStamp(d: Date | string | null | undefined): string {
  if (!d) return '';
  const iso = typeof d === 'string' ? d : d.toISOString();
  return `${iso.slice(0, 10)} ${iso.slice(11, 16)} UTC`;
}

/** Monday 00:00 UTC of the week `now` is in. */
export function lastMonday(now: Date): Date {
  const d = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate()));
  d.setUTCDate(d.getUTCDate() - ((d.getUTCDay() + 6) % 7));
  return d;
}

const plural = (n: number, one: string, many = `${one}s`) => `${n} ${n === 1 ? one : many}`;

/**
 * "Since last Monday", four lines, each from the doc: the refreshes, the
 * numbers that moved outside their range, the deck check, and what people
 * changed (pins, mappings, the schedule, the window).
 */
export function sinceLastMonday(thread: ConversationThread, now: Date): string[] {
  const doc = thread.doc || {};
  const since = lastMonday(now);
  const monday = since.toISOString().slice(0, 10);
  const recent = (doc.activity || []).filter((a) => a.at && new Date(a.at).getTime() >= since.getTime());
  const refreshes = recent.filter((a) => a.action === 'refresh');
  const next = nextRefresh(doc.schedule, now);
  const refreshLine = refreshes.length
    ? `Refreshed ${plural(refreshes.length, 'time')} since Monday ${monday} — last ${utcStamp(refreshes[refreshes.length - 1].at)}`
    : `${doc.last_refreshed_at ? `Last refreshed ${utcStamp(String(doc.last_refreshed_at))} — not since Monday ${monday}` : `Not refreshed since Monday ${monday}`}${next ? ` — next ${utcStamp(next)}` : doc.schedule?.follow ? ' — it refreshes when its report does' : ''}`;
  const movers = reviewSections(thread).filter((s) => s.kind === 'claim' && s.flag === 'significant');
  const moveLine = movers.length
    ? `${plural(movers.length, 'number')} moved outside ${movers.length === 1 ? 'its' : 'their'} normal range: ${movers.slice(0, 3).map((s) => `${sectionTitle(s)} ${moveText(s) || ''}`.trim()).join(', ')}${movers.length > 3 ? ', …' : ''}`
    : 'No number moved outside its normal range at the last refresh';
  const check = doc.deck_check;
  const deckLine = check
    ? `Checked against ${deckName(check)}: ${deckCountsText(deckCounts(thread, check))}`
    : 'Not checked against a deck — import one to check its numbers against the warehouse';
  const count = (action: string) => recent.filter((a) => a.action === action).length;
  const changes = [
    count('pin') && `${count('pin')} pinned`,
    count('unpin') && `${count('unpin')} unpinned`,
    count('mapped') && plural(count('mapped'), 'definition mapped', 'definitions mapped'),
    count('schedule') && 'schedule changed',
    count('window rule') && 'window rule changed',
  ].filter(Boolean) as string[];
  const changeLine = changes.length ? changes.join(' · ') : 'No pins, mappings or schedule changes since Monday';
  return [refreshLine, moveLine, deckLine, changeLine];
}

export interface ReviewStatus { label: 'Live' | 'Watching' | 'Checked' | 'Draft' | 'New'; tone: StatusTone }

/** A card's status: following a live report · refreshing on a schedule · checked against a deck · a draft · not started. */
export function reviewStatus(thread: ConversationThread): ReviewStatus {
  const stage = reviewStage(thread);
  const doc = thread.doc || {};
  if (stage === 'new') return { label: 'New', tone: 'off' };
  if (doc.schedule?.follow === 'source') return { label: 'Watching', tone: 'running' };
  // Live means refreshing on a weekday: a review whose refreshing was stopped keeps its stage, not the word
  if (stage === 'live' && doc.schedule?.weekday) return { label: 'Live', tone: 'running' };
  if (doc.deck_check) return { label: 'Checked', tone: 'go' };
  return { label: 'Draft', tone: 'draft' };
}

/**
 * The deck check a review was built from. An unstarted review may hold part
 * of a grading that was left (a reload between pages) — that is not a check.
 */
export function builtDeckCheck(thread: ConversationThread | null | undefined): DeckCheck | null {
  return reviewStage(thread) !== 'new' ? thread?.doc?.deck_check || null : null;
}

/** Who started the review, as its own activity records it. */
export function reviewOwner(thread: ConversationThread): string | null {
  // the doc's own stamp; reviews started before it fall back to the start entry while the bounded log still holds it
  const by = thread.doc?.started_by || (thread.doc?.activity || []).find((a) => a.action === 'start')?.by || null;
  // an account with no name is recorded by its id — no name to show, rather than a UUID
  return by && !/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(by) ? by : null;
}

/** The card's second line: where it came from, what it stands on, when it last refreshed. */
export function reviewSubline(thread: ConversationThread, perspectiveName: (id: string) => string): string {
  const doc = { ...(thread.doc || {}), deck_check: builtDeckCheck(thread) };
  const parts: string[] = [];
  if (doc.source) parts.push(`Live from Tableau · ${doc.source.name || doc.source.workbook || 'a report'}`);
  else if (doc.deck_check) parts.push(`Imported · ${deckName(doc.deck_check)}`);
  else if (doc.template_name || doc.template_id) parts.push(`${doc.template_name || doc.template_id}`);
  const grounding = (doc.perspectives && doc.perspectives.length ? doc.perspectives : [doc.perspective_id || thread.perspectiveId]).filter(Boolean) as string[];
  if (grounding.length) parts.push(`grounded on ${perspectiveName(grounding[0])}${grounding.length > 1 ? ` + ${grounding.length - 1} more` : ''}`);
  parts.push(doc.last_refreshed_at ? `refreshed ${String(doc.last_refreshed_at).slice(0, 10)}` : 'not refreshed yet');
  return parts.join(' · ');
}

/** The card's check line: "Checked against X · 1 stale · 1 different definition", or "All 9 numbers match". */
export function deckSummary(thread: ConversationThread): string | null {
  const check = builtDeckCheck(thread);
  if (!check || !check.rows.length) return null;
  const c = deckCounts(thread, check);
  if (c.matches === c.total) return `Checked against ${deckName(check)} · all ${c.total} numbers match`;
  const rest = [c.stale && `${c.stale} stale`, c.definition && plural(c.definition, 'different definition'), c.mapped && `${c.mapped} mapped`, c.needs && `${c.needs} need${c.needs === 1 ? 's' : ''} you`, c.skipped && `${c.skipped} not graded`]
    .filter(Boolean).join(' · ');
  return `Checked against ${deckName(check)} · ${c.matches} match · ${rest}`;
}

/** A refreshed number's values, oldest first — what its sparkline draws. */
export function tileSeries(section: ReviewSection): number[] {
  const values = [...(section.history || []).map((h: any) => h?.value), section.value];
  return values.map((v) => (typeof v === 'number' ? v : Number(v))).filter((v) => Number.isFinite(v));
}
